/*
 * Mermaid's Magic — cinematic runtime (cine.js)
 * <cine-video>, <cine-stack>, header theme switch, reveal fallback.
 * Vanilla, no deps. Works as a module or classic script (no import/export).
 *
 * Markup contracts:
 *   <cine-video data-src-720="…" data-src-1080="…" data-poster="…" [data-aspect="16/9"]>
 *     [<img class="cine-video__poster" …>]   optional server-rendered poster; created from data-poster otherwise
 *   </cine-video>
 *   <cine-stack>
 *     <div class="cine-stack" style="--steps:N">
 *       <div class="cine-stack__stage" tabindex="0"> <article class="cine-stack__card" style="--i:0">…</article> … </div>
 *       <div class="cine-stack__counter"><span data-stack-current>01</span> / 0N</div>
 *       [<button data-stack-prev>] [<button data-stack-next>]
 *     </div>
 *   </cine-stack>
 *   <section data-header-theme="dark|light"> → html[data-header-theme]
 */
(function () {
  if (window.__cineInit) return;
  window.__cineInit = true;

  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileMQ = window.matchMedia('(max-width: 749px)');
  var reduced = function () { return reduceMQ.matches; };
  var hasIO = 'IntersectionObserver' in window;

  /* ── <cine-video> ──
     Poster first: no video is created until the window load event + an idle slot, so a hero film
     never competes with the LCP poster. Save-Data / 2g / 3g connections keep the poster only. */
  var slowNet = function () {
    var c = navigator.connection;
    return !!(c && (c.saveData || /(^|-)2g$|^3g$/.test(c.effectiveType || '')));
  };
  var afterLoad = new Promise(function (resolve) {
    if (document.readyState === 'complete') resolve();
    else window.addEventListener('load', function () { resolve(); }, { once: true });
  }).then(function () {
    return new Promise(function (resolve) {
      if ('requestIdleCallback' in window) window.requestIdleCallback(function () { resolve(); }, { timeout: 2500 });
      else setTimeout(resolve, 300);
    });
  });
  var videoIO = hasIO ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) e.target.play(); else e.target.pause();
    });
  }, { rootMargin: '200px 0px' }) : null;

  class CineVideo extends HTMLElement {
    connectedCallback() {
      var poster = this.dataset.poster;
      if (this.dataset.aspect && !this.style.aspectRatio) this.style.aspectRatio = this.dataset.aspect;
      if (poster && !this.querySelector('img')) {
        var img = document.createElement('img');
        img.className = 'cine-video__poster';
        img.src = poster;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        this.prepend(img);
      }
      if (reduced()) return;
      if (videoIO) videoIO.observe(this); else this.play();
    }
    disconnectedCallback() {
      if (videoIO) videoIO.unobserve(this);
    }
    _ensure() {
      if (this._video) return this._video;
      if (reduced()) return null;
      var conn = navigator.connection;
      var hi = window.innerWidth >= 1024 && !(conn && conn.saveData);
      var s720 = this.getAttribute('data-src-720'), s1080 = this.getAttribute('data-src-1080');
      var src = (hi && s1080) || s720 || s1080;
      if (!src) return null;
      var v = document.createElement('video');
      v.muted = true;
      v.defaultMuted = true;
      v.loop = true;
      v.playsInline = true;
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      v.setAttribute('loop', '');
      v.setAttribute('preload', 'none');
      v.setAttribute('aria-hidden', 'true');
      v.className = 'cine-video__media';
      if (this.dataset.poster) v.poster = this.dataset.poster;
      v.src = src;
      this.append(v);
      this.classList.add('is-loaded');
      return (this._video = v);
    }
    play() {
      this._want = true;
      if (slowNet()) return;
      var self = this;
      afterLoad.then(function () {
        if (!self._want) return;
        var v = self._ensure();
        if (!v) return;
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      });
    }
    pause() {
      this._want = false;
      if (this._video) this._video.pause();
    }
  }

  /* ── <cine-stack> ── */
  var stacks = new Set();
  var pad = function (n) { return (n < 9 ? '0' : '') + (n + 1); };
  var horizontal = function () { return mobileMQ.matches || reduced(); };

  class CineStack extends HTMLElement {
    connectedCallback() {
      this.root = this.querySelector('.cine-stack') || this;
      this.stage = this.root.querySelector('.cine-stack__stage');
      this.cards = Array.prototype.slice.call(this.root.querySelectorAll('.cine-stack__card'));
      this.counter = this.root.querySelector('[data-stack-current]');
      this.k = -1;
      if (!this.stage || !this.cards.length) return;
      if (!this.stage.hasAttribute('tabindex')) this.stage.tabIndex = 0;

      this._onKey = this._onKey.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onStage = this._onStage.bind(this);
      this.stage.addEventListener('keydown', this._onKey);
      this.root.addEventListener('click', this._onClick);
      this.stage.addEventListener('scroll', this._onStage, { passive: true });
      stacks.add(this);
      this.update();
    }
    disconnectedCallback() {
      stacks.delete(this);
      if (!this.stage) return;
      this.stage.removeEventListener('keydown', this._onKey);
      this.root.removeEventListener('click', this._onClick);
      this.stage.removeEventListener('scroll', this._onStage);
    }
    get steps() { return this.cards.length; }
    _top() { return this.root.getBoundingClientRect().top + window.scrollY; }
    _cardStep() {
      var c = this.cards;
      return (c.length > 1 ? c[1].offsetLeft - c[0].offsetLeft : c[0].offsetWidth) || 1;
    }
    _onStage() {
      if (horizontal()) this.update();
    }
    update() {
      var k;
      if (horizontal()) {
        k = Math.round(this.stage.scrollLeft / this._cardStep());
      } else {
        // +.16 matches cine-primitives.css, where card k lands 15% before its step boundary.
        k = Math.floor((window.scrollY - this._top()) / window.innerHeight + 0.16);
      }
      this.set(Math.max(0, Math.min(this.steps - 1, k)));
    }
    set(k) {
      if (k === this.k) return;
      this.k = k;
      if (this.counter) this.counter.textContent = pad(k);
      this.root.style.setProperty('--k', k);
      var last = this.steps - 1;
      this.root.querySelectorAll('[data-stack-prev]').forEach(function (b) { b.setAttribute('aria-disabled', String(k === 0)); });
      this.root.querySelectorAll('[data-stack-next]').forEach(function (b) { b.setAttribute('aria-disabled', String(k === last)); });
      // Pinned layout stacks every card in one viewport: keep off-screen cards out of tab order and screen readers.
      var pinned = !horizontal();
      this.cards.forEach(function (c, i) { c.inert = pinned && i !== k; });
      if (reduced()) return;
      this.cards.forEach(function (c, i) {
        c.classList.toggle('is-active', i === k);
        c.classList.toggle('is-past', i < k);
      });
    }
    go(k) {
      k = Math.max(0, Math.min(this.steps - 1, k));
      var behavior = reduced() ? 'auto' : 'smooth';
      if (horizontal()) this.stage.scrollTo({ left: k * this._cardStep(), behavior: behavior });
      else window.scrollTo({ top: this._top() + k * window.innerHeight, behavior: behavior });
    }
    _onKey(e) {
      var d = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
      if (!d) return;
      var next = this.k + d;
      if (next < 0 || next >= this.steps) return;
      e.preventDefault();
      this.go(next);
    }
    _onClick(e) {
      if (e.target.closest('[data-stack-prev]')) this.go(this.k - 1);
      else if (e.target.closest('[data-stack-next]')) this.go(this.k + 1);
    }
  }

  if (!customElements.get('cine-video')) customElements.define('cine-video', CineVideo);
  if (!customElements.get('cine-stack')) customElements.define('cine-stack', CineStack);

  /* ── Header theme switch ── */
  var BAND = 80;
  var PROBE = 40;
  var themeSections = [];
  var themeIO = null;

  function updateHeaderTheme() {
    var theme = 'dark';
    for (var i = 0; i < themeSections.length; i++) {
      var r = themeSections[i].getBoundingClientRect();
      if (r.top <= PROBE && r.bottom > PROBE) theme = themeSections[i].dataset.headerTheme === 'light' ? 'light' : 'dark';
    }
    var root = document.documentElement;
    if (root.dataset.headerTheme !== theme) root.dataset.headerTheme = theme;
  }

  function initHeaderTheme() {
    if (themeIO) themeIO.disconnect();
    themeSections = Array.prototype.slice.call(document.querySelectorAll('[data-header-theme]:not(html)'));
    if (hasIO && themeSections.length) {
      var bottom = -Math.max(0, window.innerHeight - BAND);
      themeIO = new IntersectionObserver(updateHeaderTheme, { rootMargin: '0px 0px ' + bottom + 'px 0px' });
      themeSections.forEach(function (s) { themeIO.observe(s); });
    }
    updateHeaderTheme();
  }

  /* ── Reveal fallback (same contract as theme.js Module 1; adding the class twice is harmless) ── */
  var revealIO = hasIO ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-visible');
      revealIO.unobserve(e.target);
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -30px 0px' }) : null;

  function initReveal(scope) {
    (scope || document).querySelectorAll('[data-reveal]:not(.is-visible), .reveal:not(.is-visible)').forEach(function (el) {
      if (el.hasAttribute('data-count')) return;
      if (revealIO) revealIO.observe(el); else el.classList.add('is-visible');
    });
  }

  /* ── Shared scroll / resize loop ── */
  var ticking = false;
  function frame() {
    ticking = false;
    updateHeaderTheme();
    if (!horizontal()) stacks.forEach(function (s) { s.update(); });
  }
  function schedule() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(frame);
  }
  window.addEventListener('scroll', schedule, { passive: true });

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      initHeaderTheme();
      stacks.forEach(function (s) { s.k = -1; s.update(); });
    }, 150);
  });

  var onMQ = function () { stacks.forEach(function (s) { s.k = -1; s.update(); }); };
  [mobileMQ, reduceMQ].forEach(function (mq) {
    if (mq.addEventListener) mq.addEventListener('change', onMQ); else if (mq.addListener) mq.addListener(onMQ);
  });

  /* ── GA4 add_to_cart for AJAX quick-adds (flavor deck, collection cards).
        The PDP fires its own event and marks its broadcast tracked:true. ── */
  document.addEventListener('cart:updated', function (e) {
    var d = e.detail || {};
    var it = d.item;
    if (d.tracked || !it || typeof window.gtag !== 'function') return;
    var price = (it.final_price != null ? it.final_price : it.price) / 100;
    var qty = it.quantity || 1;
    window.gtag('event', 'add_to_cart', {
      currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
      value: price * qty,
      items: [{
        item_id: String(it.product_id || it.id || ''),
        item_name: it.product_title || it.title || '',
        item_variant: it.variant_title || '',
        price: price,
        quantity: qty
      }]
    });
  });

  /* ── Header cart count: AJAX quick-adds (e.g. cine-flavor-deck) dispatch 'cart:updated' ── */
  document.addEventListener('cart:updated', function (e) {
    var count = e.detail && e.detail.item_count;
    if (typeof count !== 'number') return;
    document.querySelectorAll('.header__icon--cart').forEach(function (a) {
      var badge = a.querySelector('.header__cart-badge');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'header__cart-badge';
        badge.setAttribute('aria-hidden', 'true');
        a.appendChild(badge);
      }
      badge.textContent = count > 99 ? '99+' : String(count);
      badge.hidden = count < 1;
      a.setAttribute('aria-label', (a.dataset.label || 'Cart') + ' (' + count + ')');
    });
  });

  function init() {
    initHeaderTheme();
    initReveal();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initHeaderTheme();
    initReveal(e.target && e.target.querySelectorAll ? e.target : document);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
